import React, { useMemo, useState, useCallback } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { getStateCodeFromGeo, getStateName } from '../../utils/stateFips';
import './USStateHeatMap.css';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

const BASE_FILL = '#2c2b2b';
const SELECT_SEAMLESS_FILL = '#1a1a1a';
const SEAMLESS_FILL = '#000000';
const HEAT_RGB = { r: 246, g: 193, b: 43 };
const BASE_RGB = { r: 44, g: 43, b: 43 };
const SEAMLESS_RGB = { r: 0, g: 0, b: 0 };

const getHeatColor = (value, max, seamless = false) => {
  const emptyFill = seamless ? SEAMLESS_FILL : BASE_FILL;
  if (!value || max <= 0) {
    return emptyFill;
  }

  const base = seamless ? SEAMLESS_RGB : BASE_RGB;
  const t = Math.min(value / max, 1);
  const r = Math.round(base.r + (HEAT_RGB.r - base.r) * t);
  const g = Math.round(base.g + (HEAT_RGB.g - base.g) * t);
  const b = Math.round(base.b + (HEAT_RGB.b - base.b) * t);
  return `rgb(${r}, ${g}, ${b})`;
};

const USStateHeatMap = ({
  stateData = {},
  selectedState = '',
  onStateSelect,
  interactive = true,
  showLegend = false,
  showTooltip = true,
  variant = 'heatmap',
  seamless = false,
  className = ''
}) => {
  const emptyFill = seamless
    ? (variant === 'select' ? SELECT_SEAMLESS_FILL : SEAMLESS_FILL)
    : BASE_FILL;
  const [hoveredState, setHoveredState] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const maxCount = useMemo(() => {
    const values = Object.values(stateData);
    return values.length ? Math.max(...values, 1) : 1;
  }, [stateData]);

  const activeState = hoveredState || null;

  const getFill = useCallback(
    (stateCode) => {
      if (!stateCode) {
        return emptyFill;
      }

      if (variant === 'select') {
        if (stateCode === selectedState) {
          return seamless ? 'rgba(246, 193, 43, 0.8)' : 'var(--primary-color)';
        }
        if (stateCode === hoveredState) {
          return 'rgba(246, 193, 43, 0.35)';
        }
        return emptyFill;
      }

      return getHeatColor(stateData[stateCode] || 0, maxCount, seamless);
    },
    [variant, selectedState, hoveredState, stateData, maxCount, seamless, emptyFill]
  );

  const getStroke = useCallback(
    (stateCode) => {
      if (stateCode === selectedState) {
        return seamless && variant === 'select'
          ? 'rgba(246, 193, 43, 0.55)'
          : 'var(--primary-strong)';
      }
      if (stateCode === hoveredState) {
        return seamless
          ? 'rgba(246, 193, 43, 0.25)'
          : 'rgba(246, 193, 43, 0.8)';
      }
      if (seamless && variant === 'heatmap') {
        const count = stateData[stateCode] || 0;
        return count > 0 ? 'rgba(246, 193, 43, 0.2)' : 'rgba(255, 255, 255, 0.06)';
      }
      if (seamless && variant === 'select') {
        return 'rgba(255, 255, 255, 0.22)';
      }
      if (seamless) {
        return 'rgba(255, 255, 255, 0.06)';
      }
      return 'rgba(255, 255, 255, 0.12)';
    },
    [selectedState, hoveredState, seamless, variant, stateData]
  );

  const handleMouseMove = (event) => {
    if (!showTooltip) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    setTooltipPos({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    });
  };

  const handleStateClick = (stateCode) => {
    if (!interactive || !onStateSelect || !stateCode) {
      return;
    }

    onStateSelect(stateCode, getStateName(stateCode));
  };

  const tooltipLabel = activeState ? getStateName(activeState) : '';
  const tooltipCount =
    variant === 'heatmap' && activeState ? stateData[activeState] || 0 : null;

  return (
    <div
      className={`us-state-heatmap ${className}`.trim()}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setHoveredState(null)}
    >
      <ComposableMap
        projection="geoAlbersUsa"
        className="us-state-heatmap__map"
      >
        <Geographies geography={GEO_URL}>
          {({ geographies }) =>
            geographies.map((geo) => {
              const stateCode = getStateCodeFromGeo(geo);
              const isSelected = stateCode === selectedState;
              const isHovered = stateCode === hoveredState;

              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill={getFill(stateCode)}
                  stroke={getStroke(stateCode)}
                  strokeWidth={isSelected ? 1.4 : isHovered ? 1.1 : 0.6}
                  className={[
                    'us-state-heatmap__state',
                    interactive ? 'us-state-heatmap__state--interactive' : '',
                    isSelected ? 'us-state-heatmap__state--selected' : ''
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onMouseEnter={() => stateCode && setHoveredState(stateCode)}
                  onMouseLeave={() => setHoveredState(null)}
                  onClick={() => handleStateClick(stateCode)}
                  style={{
                    default: { outline: 'none', transition: 'fill 180ms ease, stroke 180ms ease' },
                    hover: { outline: 'none', cursor: interactive ? 'pointer' : 'default' },
                    pressed: { outline: 'none' }
                  }}
                />
              );
            })
          }
        </Geographies>
      </ComposableMap>

      {showTooltip && activeState && (
        <div
          className="us-state-heatmap__tooltip"
          style={{ left: tooltipPos.x, top: tooltipPos.y }}
          aria-hidden="true"
        >
          <span className="us-state-heatmap__tooltip-name">{tooltipLabel}</span>
          {tooltipCount !== null && (
            <span className="us-state-heatmap__tooltip-count">
              {tooltipCount} design{tooltipCount === 1 ? '' : 's'}
            </span>
          )}
        </div>
      )}

      {showLegend && variant === 'heatmap' && (
        <div className="us-state-heatmap__legend" aria-hidden="true">
          <span>0</span>
          <div className="us-state-heatmap__legend-bar" />
          <span>{maxCount}</span>
        </div>
      )}
    </div>
  );
};

export default USStateHeatMap;
